import { useMemo, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Box, Container, CircularProgress, Alert, Typography } from '@mui/material';
import { Provider } from 'react-redux';
import {
  useGetMenuItemsQuery,
  findMenuItemByLink,
  findParentPathMenuItem,
  findAncestorMenuItem,
  findParentMenuItem,
  findMenuItemByLinkId,
  useGetGridMetadataQuery,
  useGetMenuItemStructureQuery,
} from '../menu';
import type { MenuItem } from '../menu';
import { AppBreadcrumbs } from '../../components/common/AppBreadcrumbs';
import { DetailForm, useDetailFormData } from '../detail-form';
import { DetailPageHeading } from '../detail-form/DetailPageHeading';
import Kanban from '../kanban/projectsKanban/Kanban';
import { store as kanbanStore } from '../kanban/projectsKanban/store';
import Localization from '../kanban/shared/components/localization';
import type { KanbanView } from '../kanban/projectsKanban/contexts/KanbanViewContext';
import api from '../../services/api';
import { GridPage } from '../grid/GridPage';
import { PageType } from '../../types/pageType';
import { FileStoragePage, FileManagerPage } from '../file-storage';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { KanbanThemeProvider } from '../kanban/projectsKanban/styles/KanbanThemeProvider.tsx';
import { SummaryPage } from '../summary/SummaryPage';
import { useGetDashboardIdQuery } from '../summary/api/summaryApi';
import { useAppSelector } from '../../app/store';
import { hasCommand } from '../grid/commands/utils';
import { sxColFill, sxColFillHidden, sxColFullHidden } from '../../theme/sxMixins';
import { usePageTitle } from '../../hooks/usePageTitle';
import { resolveDetailPageTitle } from '../detail-form/detailPageTitle';
import { useResolveMenuButton } from './useResolveMenuButton';
import { isFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';

const extractIdFromPathname = (pathname: string): string | undefined => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return undefined;

  const lastSegment = segments[segments.length - 1];

  // Check if it's 'add' mode
  if (lastSegment === 'add') return undefined;

  // Numeric ID, finance email history key (o123 / i456), or GUID
  const isNumericId = /^\d+$/.test(lastSegment);
  if (isNumericId || isFinanceEmailHistoryId(lastSegment)) return lastSegment;

  // Check if it's a GUID
  const isGuidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    lastSegment
  );
  if (isGuidId) return lastSegment;

  return undefined;
};

const findMenuItemById = (items: MenuItem[], id: number): MenuItem | null => {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.childs.length > 0) {
      const found = findMenuItemById(item.childs, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * KanbanTaskDetail — inline detail form for a kanban task.
 *
 * Lifecycle:
 *  1. Kanban calls onViewChange({ type:'task-detail', taskId, editTaskRouteId })
 *  2. DataSurfacePage unmounts the board and mounts this component.
 *  3. We fetch the form definition (detailMetadata) using editTaskRouteId
 *     (= the Umbraco node ID stored in kanban routes config).
 *  4. We fetch the task record (entity) using the form's contentKey + taskId.
 *  5. We fetch lookup data for every relation/dropdown field.
 *  6. Once all three are ready we render <DetailForm>.
 *
 * Why editTaskRouteId can be 0:
 *   Routes are loaded async via fetchKanbanRoutes.  If the user somehow clicks
 *   before that request completes the ID arrives as 0.  We bail early with a
 *   clear message rather than firing bad API calls.
 */
export const KanbanTaskDetail = ({
  taskId,
  editTaskRouteId,
  developersNodeKey,
  onBack,
}: {
  taskId: number;
  editTaskRouteId: number;
  developersNodeKey?: string;
  onBack: () => void;
}) => {
  // Guard: kanban routes not yet loaded — editTaskRouteId is still 0.
  const {
    detailMetadata,
    entity,
    initialValues,
    lookupMaps,
    lookupOptionMaps,
    lookupDataArrays,
    isLoading,
    isError,
  } = useDetailFormData({
    detailFormId: editTaskRouteId,
    entityId: taskId,
    mode: 'edit',
    skip: !editTaskRouteId,
    projectScopedLookupOptions: developersNodeKey ? { developersNodeKey } : undefined,
  });

  // Set the browser tab title based on the task's title (if available) or fallback to default
  usePageTitle(
    detailMetadata?.title ? resolveDetailPageTitle(detailMetadata.title, initialValues) : undefined
  );

  if (!editTaskRouteId) {
    return (
      <Alert severity="warning">
        Task detail form not configured. Make sure the kanban page has an{' '}
        <strong>editTaskRouteId</strong> set in its Umbraco routes config.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !detailMetadata || !entity) {
    return (
      <Alert severity="error">
        Failed to load task details. Check that <strong>editTaskRouteId={editTaskRouteId}</strong>{' '}
        points to a valid detail-form node in Umbraco.
      </Alert>
    );
  }

  return (
    <>
      <DetailPageHeading template={detailMetadata.title} values={initialValues} />
      <DetailForm
        metadata={detailMetadata}
        initialValues={initialValues}
        mode="edit"
        basePath="/"
        lookupMaps={lookupMaps}
        lookupOptionMaps={lookupOptionMaps}
        lookupDataArrays={lookupDataArrays}
        parentId={String(taskId)}
        detailFormKey={detailMetadata.contentKey}
        richTextOptions={{
          enableMediaUpload: true,
          uploadContextId: taskId,
        }}
        showDelete
        showArchive
        onSaveSuccess={() => {}}
        onSuccess={onBack}
      />
    </>
  );
};

export const DataSurfacePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const params = useParams({ strict: false }) as { id?: string };
  const search = useSearch({ strict: false }) as {
    gridContentKey?: string;
    detailFormKey?: string;
    reviewId?: string;
    parentId?: string;
    id?: string;
    bc?: string;
    startDate?: string;
    endDate?: string;
    DetailedData?: string;
    parentEntityId?: string;
    parentDetailFormKey?: string;
  };

  // ── Board-details URL detection ──────────────────────────────────────────
  // Handles legacy URLs of the form /management/board/details?id=82686&bc=4882.
  // These links originated in the old jQuery/Umbraco app.  bc = numeric Umbraco
  // node ID of the kanban board page; id = the task record ID to open.
  const pathSegments = pathname.split('/').filter(Boolean);
  const lastPathSegment = pathSegments[pathSegments.length - 1];
  // Request-level detection: URL contains /.../details and an `id` query param.
  // Parse and validate numeric values strictly (only digits) to avoid accepting
  // values like "123abc" or "0". Treat invalid/missing values as 0 and ignore
  // the deep link unless the parsed ID is a positive integer.
  const parsePositiveInt = (v?: string | number | null): number => {
    if (v == null) return 0;
    const s = String(v).trim();
    if (!/^[0-9]+$/.test(s)) return 0;
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.trunc(n);
  };

  const requestedBoardTaskId = parsePositiveInt(search?.id);
  const requestedBoardNumericNodeId = parsePositiveInt(search?.bc);
  const isBoardDetailsRequest = lastPathSegment === 'details' && requestedBoardTaskId > 0;

  if (lastPathSegment === 'details' && !!search?.id && requestedBoardTaskId === 0) {
    console.warn('DataSurfacePage: ignoring invalid board details id:', search?.id);
  }

  if (lastPathSegment === 'details' && !!search?.bc && requestedBoardNumericNodeId === 0) {
    console.warn('DataSurfacePage: ignoring invalid board context (bc):', search?.bc);
  }

  // Extract ID from params
  const paramId = params.id;

  // Get grid metadata from search params (passed from GridPage)
  const gridContentKeyFromState = search?.gridContentKey;

  // Fallback: extract ID from pathname if route parameter not available
  // This handles the wildcard route scenario where :id is not captured
  const id = useMemo(() => {
    if (paramId) return paramId;
    return extractIdFromPathname(pathname);
  }, [paramId, pathname]);

  const mode: 'add' | 'edit' = id ? 'edit' : 'add';
  const standaloneDetailFormKey = search?.detailFormKey?.trim() || undefined;
  const isStandaloneDetail = Boolean(standaloneDetailFormKey);
  const isWorkspaceFilesRoute = pathname.includes('/workspace/files/');
  const normalizedPathname = pathname.replace(/\/+$/, '');

  // Extract base path (e.g., "/employees/123" -> "/employees")
  // Only strip the last segment when it is an ID (numeric/GUID), the literal
  // "add", or the internal details view segment "details". This ensures
  // navigating back from a canonical details URL returns to the parent page
  // (e.g. "/management/board") instead of staying on "/management/board/details".
  const basePath = useMemo(() => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 0) return '/';
    const last = parts[parts.length - 1];
    const isIdOrAdd =
      last === 'add' ||
      last === 'details' ||
      /^\d+$/.test(last) ||
      isFinanceEmailHistoryId(last) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(last);
    if (isIdOrAdd) parts.pop();
    return '/' + parts.join('/');
  }, [pathname]);

  // Fetch menu items
  const { data: menuItems = [], isLoading: menuLoading } = useGetMenuItemsQuery(undefined);

  const exactMenuItem = useMemo(
    () => findMenuItemByLink(menuItems, basePath),
    [menuItems, basePath]
  );

  // Find menu item for this page
  const menuItem = useMemo(() => {
    if (exactMenuItem) return exactMenuItem;
    return (
      findParentPathMenuItem(menuItems, basePath) ??
      findAncestorMenuItem(menuItems, basePath) ??
      null
    );
  }, [exactMenuItem, menuItems, basePath]);

  const { effectiveMenuItemId, effectiveNodeKey } = useResolveMenuButton({
    menuItem: exactMenuItem,
    menuItems,
    basePath,
  });

  const effectiveGridContentKey = gridContentKeyFromState ?? effectiveNodeKey ?? undefined;
  const reviewContextId = search.parentId ?? search.reviewId;

  // Get menuItemId for fetching grid metadata
  const menuItemId = useMemo(() => {
    if (exactMenuItem) {
      return exactMenuItem.contentType === 'zMenuItem' ? exactMenuItem.linkId : exactMenuItem.id;
    }
    if (effectiveMenuItemId) return effectiveMenuItemId;
    return undefined;
  }, [exactMenuItem, effectiveMenuItemId]);

  const linkedMenuItem = useMemo(() => {
    if (!menuItem?.linkId) return null;
    return findMenuItemById(menuItems, menuItem.linkId);
  }, [menuItem, menuItems]);

  const summaryBreadcrumbPath = useMemo(() => {
    const parentId = parsePositiveInt(search?.bc);
    if (!parentId) return null;

    // Try direct lookup first (bc = visible menu item id)
    const directItem = findMenuItemById(menuItems, parentId);
    const resolvedItem = directItem ?? findMenuItemByLinkId(menuItems, parentId);

    if (!resolvedItem?.link) return null;

    const resolvedLinkedItem = resolvedItem.linkId
      ? findMenuItemById(menuItems, resolvedItem.linkId)
      : null;

    const isSummary =
      resolvedItem.contentType === 'zSummaryDashboard' ||
      resolvedLinkedItem?.contentType === 'zSummaryDashboard' ||
      // Hidden linked node: treat zMenuItem→linkId=parentId as summary
      // since Weekly/Monthly Summary items always have this shape
      (resolvedItem.contentType === 'zMenuItem' && resolvedItem.linkId === parentId);

    return isSummary ? resolvedItem.link : null;
  }, [menuItems, search?.bc]);

  const summaryFromMenu = (() => {
    const candidateMenuItem = exactMenuItem ?? menuItem;
    const dashboardId =
      candidateMenuItem?.contentType === 'zSummaryDashboard'
        ? candidateMenuItem.id
        : linkedMenuItem?.contentType === 'zSummaryDashboard'
          ? candidateMenuItem?.linkId
          : undefined;
    return {
      dashboardId,
      shouldResolve:
        !id &&
        ((candidateMenuItem?.contentType === 'zSummaryDashboard' && dashboardId != null) ||
          (candidateMenuItem?.contentType === 'zMenuItem' &&
            candidateMenuItem.linkId != null &&
            (dashboardId != null || linkedMenuItem == null))),
    };
  })();

  const { data: summaryDashboardIdFromApi, isLoading: summaryRouteLoading } =
    useGetDashboardIdQuery(normalizedPathname, {
      skip: !summaryFromMenu.shouldResolve || summaryFromMenu.dashboardId != null,
    });

  const summaryDashboardId = summaryFromMenu.dashboardId ?? summaryDashboardIdFromApi ?? undefined;
  const isSummaryDashboardRoute = summaryFromMenu.shouldResolve && summaryDashboardId != null;
  const { roles: currentUserRoles, isAuthenticated } = useAppSelector((state) => state.auth);

  const showAdminFilters =
    isSummaryDashboardRoute &&
    isAuthenticated &&
    !currentUserRoles.some((role: string) => role === 'clients');

  const inferredBoardNumericNodeId = useMemo(() => {
    if (!isBoardDetailsRequest) return 0;
    if (requestedBoardNumericNodeId) return requestedBoardNumericNodeId;
    if (menuItem?.contentType === 'zKanban') return menuItem.id;
    if (linkedMenuItem?.contentType === 'zKanban') return linkedMenuItem.id;
    return 0;
  }, [isBoardDetailsRequest, requestedBoardNumericNodeId, menuItem, linkedMenuItem]);

  const isBoardDetailsUrl = isBoardDetailsRequest && inferredBoardNumericNodeId > 0;
  const boardTaskId = requestedBoardTaskId;
  const boardNumericNodeId = inferredBoardNumericNodeId;

  useEffect(() => {
    if (!isBoardDetailsUrl) return;
    try {
      const hasBc = !!search?.bc;
      if (hasBc && Number(search.bc) === boardNumericNodeId) return;

      const pathNoQuery = pathname.split('?')[0];
      const pathAlreadyHasDetails =
        pathNoQuery.endsWith('/details') || pathNoQuery.includes('/details/');
      const detailsBase = pathAlreadyHasDetails ? pathNoQuery : `${basePath}/details`;
      navigate({
        to: detailsBase,
        search: { id: boardTaskId, bc: boardNumericNodeId } as any,
        replace: true,
      });
    } catch (e) {
      console.error('DataSurfacePage: error canonicalizing board-details URL', e);
    }
  }, [
    isBoardDetailsUrl,
    boardNumericNodeId,
    boardTaskId,
    pathname,
    basePath,
    search?.bc,
    navigate,
  ]);

  const initialDateRange = useMemo(() => {
    if (!search.startDate && !search.endDate) return undefined;
    return { startDate: search.startDate, endDate: search.endDate };
  }, [search.startDate, search.endDate]);

  const pageType = useMemo((): PageType => {
    if (isBoardDetailsUrl) return PageType.DetailForm;
    if (isStandaloneDetail) return PageType.DetailForm;
    if (isSummaryDashboardRoute) return PageType.Summary;

    if (
      summaryFromMenu.shouldResolve &&
      summaryFromMenu.dashboardId == null &&
      summaryRouteLoading
    ) {
      return PageType.Summary;
    }

    // File Storage surfaces are standalone pages. They can have a node key when
    // reached through a menu button, but that key must not be resolved as grid
    // metadata because zSiteFileStorage/zSiteFileManager are not field containers.
    if (isWorkspaceFilesRoute) return PageType.FileStorage;

    if (effectiveGridContentKey) {
      if (!id && pathname.split('/').pop() !== 'add') return PageType.GridListing;
      return PageType.DetailForm;
    }

    const currentType = menuItem?.contentType;
    const linkedType = linkedMenuItem?.contentType;

    if (!menuItem) return PageType.DetailForm;

    if (currentType === 'zKanban' || linkedType === 'zKanban') return PageType.Kanban;

    if (
      currentType === 'zSiteFileStorage' ||
      currentType === 'zSiteFileManager' ||
      linkedType === 'zSiteFileStorage' ||
      linkedType === 'zSiteFileManager'
    ) {
      return PageType.FileStorage;
    }

    if (!id && pathname.split('/').pop() !== 'add') return PageType.GridListing;
    return PageType.DetailForm;
  }, [
    menuItem,
    linkedMenuItem,
    isBoardDetailsUrl,
    isStandaloneDetail,
    isSummaryDashboardRoute,
    isWorkspaceFilesRoute,
    id,
    effectiveGridContentKey,
    pathname,
    summaryRouteLoading,
    summaryFromMenu.shouldResolve,
    summaryFromMenu.dashboardId,
  ]);

  const fileMenuRootItem = useMemo(() => {
    if (pageType !== PageType.FileStorage || !menuItem) return null;
    if (menuItem.contentType === 'zMenuItem') return menuItem;
    return menuItem.id ? findParentMenuItem(menuItems, menuItem.id) : null;
  }, [pageType, menuItem, menuItems]);

  const { data: fileMenuStructure } = useGetMenuItemStructureQuery(
    { menuItemId: fileMenuRootItem?.id ?? 0 },
    { skip: pageType !== PageType.FileStorage || !fileMenuRootItem?.id }
  );

  const fileMenuButtons = useMemo(() => {
    const buttons = fileMenuStructure?.buttons ?? [];
    return buttons.filter(
      (b) => b.contentType === 'zSiteFileStorage' || b.contentType === 'zSiteFileManager'
    );
  }, [fileMenuStructure?.buttons]);

  const myFilesMenuItem = useMemo(
    () => fileMenuButtons.find((b) => b.contentType === 'zSiteFileStorage'),
    [fileMenuButtons]
  );

  const isFileManagerSurface = useMemo(() => {
    const currentType = menuItem?.contentType;
    const linkedType = linkedMenuItem?.contentType;
    return (
      currentType === 'zSiteFileManager' ||
      linkedType === 'zSiteFileManager' ||
      pathname.includes('/file-manager')
    );
  }, [menuItem?.contentType, linkedMenuItem?.contentType, pathname]);

  const { setPageHeader, clearPageHeader } = usePageHeader();
  useEffect(() => {
    if (pageType !== PageType.FileStorage || !menuItem) return;

    const fileButtons = fileMenuButtons;

    setPageHeader({
      title:
        fileButtons.find((b) => b.contentType === 'zSiteFileStorage')?.caption ?? menuItem.caption,
      navButtons: fileButtons.length > 0 ? fileButtons : undefined,
      onNavigate: (path) => navigate({ to: path }),
    });

    return () => clearPageHeader();
  }, [
    pageType,
    menuItem,
    fileMenuButtons,
    fileMenuRootItem,
    menuItems,
    setPageHeader,
    clearPageHeader,
    navigate,
    pathname,
  ]);

  const [kanbanView, setKanbanView] = useState<KanbanView>({ type: 'board' });
  const [isRedirectingToDetails, setIsRedirectingToDetails] = useState(false);

  useEffect(() => {
    if (pageType !== PageType.Kanban) return;

    const hasTaskInUrl = parsePositiveInt(search?.id) > 0;
    if (hasTaskInUrl) return;

    if (kanbanView.type === 'task-detail') {
      setKanbanView({ type: 'board' });
    }
    if (isRedirectingToDetails) {
      setIsRedirectingToDetails(false);
    }
  }, [pageType, search?.id, kanbanView.type, isRedirectingToDetails]);

  useEffect(() => {
    try {
      if (pageType !== PageType.Kanban) return;
      if (kanbanView.type !== 'task-detail') return;

      const taskId = kanbanView.taskId;
      if (!taskId) return;

      const resolvedBoardNodeId =
        requestedBoardNumericNodeId ||
        (linkedMenuItem?.contentType === 'zKanban' ? linkedMenuItem.id : 0) ||
        (menuItem?.contentType === 'zKanban' ? menuItem.id : 0);

      if (!resolvedBoardNodeId) return;

      if (
        String(search?.id) === String(taskId) &&
        String(search?.bc) === String(resolvedBoardNodeId)
      ) {
        return;
      }

      const pathNoQuery = pathname.split('?')[0];
      const pathAlreadyHasDetails =
        pathNoQuery.endsWith('/details') || pathNoQuery.includes('/details/');
      const detailsBase = pathAlreadyHasDetails ? pathNoQuery : `${basePath}/details`;

      try {
        setIsRedirectingToDetails(true);
        navigate({
          to: detailsBase,
          search: { id: taskId, bc: resolvedBoardNodeId } as any,
        });
      } catch (navErr) {
        setIsRedirectingToDetails(false);
        throw navErr;
      }
    } catch (e) {
      console.error('DataSurfacePage: error synchronizing Kanban view to details URL', e);
    }
  }, [
    pageType,
    kanbanView,
    requestedBoardNumericNodeId,
    linkedMenuItem?.contentType,
    linkedMenuItem?.id,
    menuItem?.contentType,
    menuItem?.id,
    basePath,
    pathname,
    search?.id,
    search?.bc,
    navigate,
  ]);

  // ── Board-details hooks (always called — hooks must not be conditional) ───
  // Find the kanban board menu item by its numeric node ID so we can get
  // its GUID nodeKey, which is required by the GetKanbanRoutes API.
  const boardMenuItem = useMemo<MenuItem | null>(() => {
    if (!isBoardDetailsUrl || !boardNumericNodeId) return null;
    const findById = (items: MenuItem[]): MenuItem | null => {
      for (const item of items) {
        if (item.id === boardNumericNodeId) return item;
        if (item.childs.length > 0) {
          const found = findById(item.childs);
          if (found) return found;
        }
      }
      return null;
    };
    return findById(menuItems);
  }, [isBoardDetailsUrl, boardNumericNodeId, menuItems]);

  // The Umbraco node ID of the "Edit Task" detail-form, resolved via kanban routes.
  // Starts at 0 until the API call completes.
  const [boardRouteId, setBoardRouteId] = useState<number>(0);
  const [boardDevelopersNodeKey, setBoardDevelopersNodeKey] = useState<string | undefined>();
  const [boardRouteLoading, setBoardRouteLoading] = useState(false);

  useEffect(() => {
    // Only fetch when this is a board-details URL and we know the board's GUID.
    if (!isBoardDetailsUrl || !boardMenuItem?.nodeKey) return;

    const fetchRoutes = async () => {
      setBoardRouteLoading(true);
      try {
        const res = await api.get('/umbraco/api/Kanban/GetKanbanRoutes', {
          params: { nodeKey: boardMenuItem.nodeKey },
        });
        const data = res.data as {
          Code?: number;
          code?: number;
          data?: { editTaskRouteId?: number; getDevelopersNodeKey?: string };
        };
        const routes = data?.Code === 1 || data?.code === 1 ? data.data : null;
        if (routes?.editTaskRouteId) {
          setBoardRouteId(routes.editTaskRouteId);
        }
        setBoardDevelopersNodeKey(routes?.getDevelopersNodeKey);
      } catch {
        // ignore
      } finally {
        setBoardRouteLoading(false);
      }
    };

    fetchRoutes();
  }, [isBoardDetailsUrl, boardMenuItem?.nodeKey]);

  // Fetch grid metadata dynamically
  // Prefer using contentKey from the navigation state if available (passed from GridPage)
  // Otherwise fall back to menuItemId-based lookup
  const {
    data: gridMetadata,
    isLoading: gridMetadataLoading,
    isFetching: gridMetadataFetching,
    error: gridMetadataError,
  } = useGetGridMetadataQuery(
    effectiveGridContentKey ? { nodeKey: effectiveGridContentKey } : { menuItemId: menuItemId! },
    {
      skip:
        isBoardDetailsUrl ||
        isStandaloneDetail ||
        pageType === PageType.Summary ||
        pageType === PageType.Kanban ||
        pageType === PageType.GridListing ||
        pageType === PageType.FileStorage ||
        (!effectiveGridContentKey && !menuItemId),
      refetchOnMountOrArgChange: true,
    }
  );

  //Detail form data (metadata + entity + lookups)
  const {
    detailMetadata,
    entity,
    initialValues,
    lookupMaps,
    lookupOptionMaps,
    lookupDataArrays,
    isLoading: detailLoading,
    isError: detailError,
  } = useDetailFormData({
    // A deep link can target a child detail form in add mode, where there is no entity ID.
    // Do not send the grid's default numeric detail form ID alongside its explicit key.
    detailFormId: standaloneDetailFormKey ? undefined : gridMetadata?.detailFormId,
    detailFormKey: standaloneDetailFormKey ?? gridMetadata?.detailFormKey,
    entityId: id || '',
    mode,
    gridNodeKey: gridMetadata?.contentKey ?? effectiveGridContentKey,
    skip:
      pageType === PageType.Summary ||
      pageType === PageType.Kanban ||
      pageType === PageType.GridListing ||
      pageType === PageType.FileStorage ||
      (!standaloneDetailFormKey && !gridMetadata?.detailFormId && !gridMetadata?.detailFormKey),
  });

  // Loading state
  const isLoading =
    menuLoading ||
    (summaryFromMenu.shouldResolve && summaryFromMenu.dashboardId == null && summaryRouteLoading) ||
    (isBoardDetailsUrl && boardRouteLoading) ||
    gridMetadataLoading ||
    gridMetadataFetching ||
    detailLoading;

  // Error handling
  const error = gridMetadataError || detailError;

  // Set the browser tab title based on the page type and available metadata
  const pageTitle = useMemo(() => {
    switch (pageType) {
      case PageType.FileStorage:
        return myFilesMenuItem?.caption ?? menuItem?.caption;
      case PageType.DetailForm:
        return detailMetadata?.title
          ? resolveDetailPageTitle(detailMetadata.title, initialValues)
          : menuItem?.caption;
      default:
        return menuItem?.caption;
    }
  }, [pageType, menuItem, myFilesMenuItem, detailMetadata, initialValues]);

  usePageTitle(pageTitle);

  if (isLoading) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // ── Board-details URL: render task detail form directly ──────────────────
  // Skip all the normal grid/detail form logic and go straight to
  // KanbanTaskDetail using the editTaskRouteId resolved above.
  if (isBoardDetailsUrl) {
    // Build breadcrumbs: show the board name when available, then "Edit task".
    const boardBreadcrumbs = [
      ...(boardMenuItem
        ? [
            {
              label: boardMenuItem.caption,
              href: basePath,
              onClick: () => {
                setKanbanView({ type: 'board' });
                navigate({ to: basePath });
              },
            },
          ]
        : []),
      { label: 'Edit task' },
    ];

    return (
      <Container
        maxWidth={false}
        sx={{
          pt: 1,
          px: 0,
          flex: '1 1 0',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={sxColFill}>
          <AppBreadcrumbs items={boardBreadcrumbs} sx={{ mb: 1, flexShrink: 0 }} />
          <Box sx={sxColFill}>
            <KanbanTaskDetail
              taskId={boardTaskId}
              editTaskRouteId={boardRouteId}
              developersNodeKey={boardDevelopersNodeKey}
              onBack={() => {
                setKanbanView({ type: 'board' });
                navigate({ to: basePath });
              }}
            />
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    const err = error as { data?: { message?: string }; message?: string };
    const errorMessage = err?.data?.message || err?.message || 'Failed to load form';
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Container>
    );
  }

  // ── Grid listing page ────────────────────────────────────────────────────
  // zMenuItem with linkId (shortcut to a grid) or any direct grid content type,
  // when there is no entity ID in the URL.  Render the full grid listing.
  if (pageType === PageType.GridListing) {
    return (
      <Box sx={sxColFullHidden}>
        <GridPage />
      </Box>
    );
  }

  if (pageType === PageType.Summary) {
    return <SummaryPage showAdminFilters={showAdminFilters} />;
  }

  if (pageType === PageType.FileStorage) {
    if (isFileManagerSurface) {
      return <FileManagerPage title={menuItem?.caption} />;
    }
    return (
      <FileStoragePage nodeKey={undefined} title={myFilesMenuItem?.caption ?? menuItem?.caption} />
    );
  }

  const allowDetailWithoutMenuItem = !!effectiveGridContentKey || isStandaloneDetail;

  if (!menuItem && !allowDetailWithoutMenuItem) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="error">Menu item not found for path: {basePath}</Alert>
      </Container>
    );
  }

  if (pageType === PageType.Kanban) {
    if (!menuItem) {
      return (
        <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
          <Alert severity="error">Kanban page is not linked to a menu item.</Alert>
        </Container>
      );
    }
    return (
      <Container maxWidth={false} disableGutters sx={{ pt: 1, px: 0 }}>
        {kanbanView.type === 'task-detail' ? (
          isRedirectingToDetails ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                minHeight: 0,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={sxColFill}>
              <AppBreadcrumbs
                items={[
                  { label: menuItem.caption, onClick: () => setKanbanView({ type: 'board' }) },
                  { label: kanbanView.taskName || 'Task' },
                ]}
                sx={{ mb: 0.5, flexShrink: 0 }}
              />
              <Box sx={sxColFill}>
                <KanbanTaskDetail
                  taskId={kanbanView.taskId}
                  editTaskRouteId={kanbanView.editTaskRouteId}
                  developersNodeKey={kanbanView.developersNodeKey}
                  onBack={() => setKanbanView({ type: 'board' })}
                />
              </Box>
            </Box>
          )
        ) : (
          <>
            <Provider store={kanbanStore}>
              <Localization translationNodeKey="ZSite.Frontend.Kanban">
                <KanbanThemeProvider>
                  <Kanban
                    pageKey={menuItem.nodeKey || String(menuItem.id)}
                    view={kanbanView}
                    onViewChange={setKanbanView}
                  />
                </KanbanThemeProvider>
              </Localization>
            </Provider>
          </>
        )}
      </Container>
    );
  }

  if (!isStandaloneDetail && !gridMetadata) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="error">
          Grid metadata not found
          {menuItemId != null
            ? ` for menu item ID: ${menuItemId}`
            : effectiveGridContentKey
              ? ` for grid key: ${effectiveGridContentKey}`
              : ''}
          .
        </Alert>
      </Container>
    );
  }

  if (
    !isStandaloneDetail &&
    gridMetadata &&
    !gridMetadata.detailFormId &&
    !gridMetadata.detailFormKey
  ) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="warning">
          <Typography variant="h6" gutterBottom>
            No Detail Form Configured
          </Typography>
          <Typography variant="body2" paragraph>
            This grid does not have a detail form configured in Umbraco.
          </Typography>
          <Typography variant="body2" component="div">
            Grid Info:
            <ul>
              <li>Content ID: {gridMetadata.contentId}</li>
              <li>Content Key: {gridMetadata.contentKey}</li>
              <li>Content Type: {gridMetadata.contentType}</li>
              <li>Detail Form ID: {gridMetadata.detailFormId || 'Not set'}</li>
              <li>Detail Form Key: {gridMetadata.detailFormKey || 'Not set'}</li>
            </ul>
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!detailMetadata) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="error">
          Detail form metadata not found. Make sure the grid has detailFormId or detailFormKey
          configured.
        </Alert>
      </Container>
    );
  }

  if (mode === 'edit' && !entity) {
    return (
      <Container maxWidth={false} sx={{ mt: 1, maxWidth: '1400px' }}>
        <Alert severity="error">Entity not found</Alert>
      </Container>
    );
  }

  const detailSurfaceLabel =
    menuItem?.caption ?? gridMetadata?.dataSource ?? detailMetadata.dataSource ?? 'Details';
  return (
    <Container maxWidth={false} sx={{ pt: 1, ...sxColFillHidden }}>
      <Box sx={sxColFill}>
        <AppBreadcrumbs
          items={[
            {
              label: detailSurfaceLabel,
              href: summaryBreadcrumbPath ?? basePath,
              onClick: () => {
                const destination = summaryBreadcrumbPath ?? basePath;
                try {
                  // Only write the grid fast-return marker when going back to a grid page,
                  // not when returning to a summary dashboard
                  if (!summaryBreadcrumbPath) {
                    sessionStorage.setItem(`GRID_FAST_RETURN:${destination}`, '1');
                  }
                } catch {
                  /* ignore */
                }
                navigate({ to: destination });
              },
            },
            { label: mode === 'add' ? 'Add New' : 'Edit' },
          ]}
          sx={{ mb: 0.5, flexShrink: 0 }}
        />

        <DetailPageHeading template={detailMetadata.title} values={initialValues} />

        <Box sx={sxColFillHidden}>
          <DetailForm
            key={id || 'new'}
            metadata={detailMetadata}
            initialValues={initialValues}
            mode={mode}
            basePath={basePath}
            lookupMaps={lookupMaps}
            lookupOptionMaps={lookupOptionMaps}
            lookupDataArrays={lookupDataArrays}
            parentId={mode === 'add' ? (reviewContextId ?? '') : id || ''}
            detailFormKey={standaloneDetailFormKey ?? gridMetadata?.detailFormKey}
            parentGridKey={isStandaloneDetail ? undefined : effectiveGridContentKey}
            menuItemId={menuItemId ?? undefined}
            showDelete={mode === 'edit' && hasCommand(gridMetadata?.commands, 'delete')}
            showArchive={mode === 'edit' && hasCommand(gridMetadata?.commands, 'archive')}
            linkToParentId={search?.parentEntityId}
            linkToParentDetailFormKey={search?.parentDetailFormKey}
            initialDateRange={initialDateRange}
            dataOptionsMax={gridMetadata?.dataOptionsMax}
          />
        </Box>
      </Box>
    </Container>
  );
};
